import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";

import { useMainStore } from "@/contexts/Store";

import { LibraryRecord } from "../../../stores/types/risk-register-types";
import { Icon } from "../../Elements";
import Checkbox from "../shared/Checkbox";
import SectionDropdownTitle from "../shared/SectionDropdownTitle";
import LibraryTableColumn from "./LibraryTableColumn";
import LibraryTableRow from "./LibraryTableRow";

const COLUMNS = [
  "L1 Risk Taxonomy",
  "L2 Risk Taxonomy",
  "Framework",
  "Risk Events",
  "Description",
  "Reference",
];

type LibraryTableProps = {
  title: string;
  rows: Array<LibraryRecord>;
  onSelectChange: (rowId: number, value: boolean) => void;
  onSelectAllChange: (rowIds: number[], value: boolean) => void;
  canDeleteRecords: boolean;
  isInternalWorkspace: boolean;
  onDeleteRecord: (id: number) => void;
  onCreate: () => void;
  isOpen: boolean;
  libraryType: string;
};

const LibraryTable = (props: LibraryTableProps) => {
  const mainStore = useMainStore();
  const { workspaceID } = mainStore.context;
  const { hasModuleWriteAccess } = mainStore.userPermissions;
  const [isOpen, setIsOpen] = useState(props.isOpen);
  const [recordIdToDelete, setRecordIdToDelete] = useState<number | null>(null);
  const allSelected = props.rows.every((item) => item.checked);
  const isTemplates = props.libraryType === "user_generated";
  const isEditable = isTemplates && props.isInternalWorkspace;

  const onSelectAllChange: React.ChangeEventHandler<HTMLInputElement> = () => {
    props.onSelectAllChange(
      props.rows.map((row) => row.id),
      !allSelected,
    );
  };

  const onDelete = async (id: number) => {
    await props.onDeleteRecord(id);
    setRecordIdToDelete(null);
  };

  useEffect(() => {
    setIsOpen(props.isOpen);
  }, [props.isOpen]);

  return (
    <>
      <SectionDropdownTitle
        title={props.title}
        isOpen={isOpen}
        onToggleOpen={() => setIsOpen((value) => !value)}
        rolePrefix="rr-library-table"
      />
      {isOpen && (
        <div
          className="rr-library-table-container rr-library-table-container--multi"
          data-testid="rr-library-table"
        >
          <div className="rr-library-table-columns rr-library-table-columns--multi">
            <Checkbox
              checked={allSelected}
              onChange={onSelectAllChange}
              data-testid="rr-library-main-checkbox"
              disabled={!hasModuleWriteAccess}
              padding
            />
            {COLUMNS.map((column) => (
              <LibraryTableColumn key={column} title={column} />
            ))}
          </div>
          {props.isInternalWorkspace && isTemplates && hasModuleWriteAccess && (
            <div className="create-new-record risk-register library">
              <div className="plus-sign" onClick={props.onCreate}>
                <Icon name="plus" color="generalWhite" size="de" />
              </div>
              <button
                type="button"
                onClick={props.onCreate}
                data-testid="risk-register-library-add-new"
              >
                Add New Library Item +
              </button>
            </div>
          )}
          <div className="rr-library-table-rows">
            {props.rows.map((row) => (
              <LibraryTableRow
                key={row.id}
                record={row}
                workspaceID={workspaceID}
                onChange={props.onSelectChange}
                isEditable={isEditable}
                canDeleteRecords={
                  props.canDeleteRecords && props.isInternalWorkspace
                }
                onDelete={() => onDelete(row.id)}
                onDeletePromptChange={(show) => {
                  if (show) {
                    setRecordIdToDelete(row.id);
                  } else {
                    setRecordIdToDelete(null);
                  }
                }}
                showDeletePrompt={row.id === recordIdToDelete}
                hasModuleWriteAccess={hasModuleWriteAccess}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default observer(LibraryTable);
